import React, { useState } from 'react';
import { Lead } from '../../../types/lead.types';
import { Appointment, Schedule, TimeSlot } from '../../../types/schedule.types';
import { format, addDays } from 'date-fns';
import { toast } from 'react-hot-toast';
import { scheduleFollowUpAppointment } from '../../../services/lead.service';
import { scheduleService, getAvailableSlotsForDate } from '../../../services/schedule.service';
import { SlotFullError, appointmentService } from '../../../services/appointment.service';
import { projectService } from '../../../services/project.service';
import { useAuth } from '../../../contexts/AuthContext';
import { useProject } from '../../../contexts/ProjectContext';
import { formatTimeRange } from '../../../utils/dateUtils';
import {
  X,
  Plus,
  Phone,
  Calendar,
  MapPin,
  Clock
} from 'lucide-react';

interface LeadScheduleTabProps {
  lead: Lead;
  editedLead: Lead;
  appointments: Appointment[];
  loadingAppointments: boolean;
  onAppointmentsChange: () => void;
}

const LeadScheduleTab: React.FC<LeadScheduleTabProps> = ({
  lead,
  editedLead,
  appointments,
  loadingAppointments,
  onAppointmentsChange
}) => {
  const { currentUser, userProfile } = useAuth();
  const { selectedProject } = useProject();
  const projectTimezone = selectedProject?.settings?.timezone || '';

  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [availableSlots, setAvailableSlots] = useState<{ date: string; slots: (TimeSlot & { userName: string; userId: string; scheduleId: string })[] }[]>([]);
  const [slotCounts, setSlotCounts] = useState<Map<string, number>>(new Map());
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<(TimeSlot & { userName: string; userId: string; scheduleId: string }) | null>(null);
  const [schedulingAppointment, setSchedulingAppointment] = useState(false);
  // When set, the slot picker is in "reschedule" mode for this appointment
  // (move the existing appointment to a new slot) rather than booking a new one.
  const [reschedulingAppt, setReschedulingAppt] = useState<Appointment | null>(null);

  // Reset the slot-picker modal back to its closed / empty state.
  const closeForm = () => {
    setShowScheduleForm(false);
    setReschedulingAppt(null);
    setSelectedDate('');
    setSelectedSlot(null);
  };

  // Load schedules when opening schedule form
  const loadSchedulesAndSlots = async () => {
    if (!lead.projectId) return;

    try {
      // Get project to find client users
      const project = await projectService.getProject(lead.projectId);
      if (!project) return;

      // Get schedules for client users
      const clientUsers = project.assignedUsers?.filter(
        (u: any) => u.role === 'client_user' || u.role === 'client_admin'
      ) || [];

      const projectSchedules = await scheduleService.getSchedulesByProject(lead.projectId);
      setSchedules(projectSchedules);

      // Generate available slots for the next 14 days
      const slotsPerDay: { date: string; slots: (TimeSlot & { userName: string; userId: string; scheduleId: string })[] }[] = [];

      for (let i = 0; i < 14; i++) {
        const date = addDays(new Date(), i);
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayOfWeek = date.getDay();

        const daySlots: (TimeSlot & { userName: string; userId: string; scheduleId: string })[] = [];

        for (const schedule of projectSchedules) {
          if (!schedule.isActive) continue;

          // Scope to the lead's campus: campus-tagged schedules for this lead's
          // campus, plus untagged (project-wide) schedules. Leads with no campus
          // see everything.
          if (lead.schoolSegmentId && schedule.schoolSegmentId && schedule.schoolSegmentId !== lead.schoolSegmentId) continue;

          // Find user name
          const user = clientUsers.find((u: any) => u.userId === schedule.userId);
          const userName = user?.name || schedule.userName || 'Unknown';

          // Centralized 30-min slot generator. Handles dateAvailability,
          // hourlyAvailability, and legacy timeSlots, with whole-hour expansion.
          const slots = getAvailableSlotsForDate(schedule, date);
          const capacity = schedule.defaultSlotCapacity ?? 1;
          for (const { startTime, endTime } of slots) {
            daySlots.push({
              dayOfWeek,
              startTime,
              endTime,
              capacity,
              isRecurring: false,
              eventType: 'meeting',
              userName,
              userId: schedule.userId,
              scheduleId: schedule.id || '',
            });
          }
        }

        if (daySlots.length > 0) {
          slotsPerDay.push({ date: dateStr, slots: daySlots });
        }
      }

      setAvailableSlots(slotsPerDay);

      // Tally existing appointments per slot so the picker can show "X/Y
      // booked" badges and disable full slots.
      const projectAppointments = await appointmentService.getAppointmentsByProject(lead.projectId);
      const counts = new Map<string, number>();
      projectAppointments.forEach(apt => {
        if (apt.status === 'cancelled' || apt.status === 'no-show') return;
        const campus = (apt as { schoolSegmentId?: string }).schoolSegmentId || 'all';
        const key = `${apt.date}-${apt.startTime}-${apt.endTime}-${apt.userId}-${campus}`;
        counts.set(key, (counts.get(key) || 0) + 1);
      });
      setSlotCounts(counts);
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  };

  // Handle scheduling a follow-up appointment
  const handleScheduleAppointment = async () => {
    if (!currentUser || !selectedSlot || !selectedDate) {
      toast.error('Please select a time slot');
      return;
    }

    // Capacity comes from the schedule that owns the selected slot.
    const ownerSchedule = schedules.find(s => s.userId === selectedSlot.userId);
    const slotCapacity = ownerSchedule?.defaultSlotCapacity ?? 1;

    setSchedulingAppointment(true);
    try {
      await scheduleFollowUpAppointment(
        lead.id,
        {
          date: selectedDate,
          startTime: selectedSlot.startTime,
          endTime: selectedSlot.endTime,
          eventType: selectedSlot.eventType || 'tour',
          clientUserId: selectedSlot.userId,
          clientUserName: selectedSlot.userName,
          scheduleId: selectedSlot.scheduleId,
          slotCapacity,
        },
        currentUser.uid,
        userProfile?.name || 'User'
      );

      toast.success('Follow-up appointment scheduled!');
      closeForm();
      onAppointmentsChange();
    } catch (error) {
      if (error instanceof SlotFullError) {
        toast.error('This slot just filled up — please pick another.');
        await loadSchedulesAndSlots();
      } else {
        console.error('Error scheduling appointment:', error);
        toast.error('Failed to schedule appointment');
      }
    } finally {
      setSchedulingAppointment(false);
    }
  };

  // Move an existing appointment to the newly selected slot. Reuses the same
  // slot picker as follow-up scheduling; the service handles releasing the old
  // slot lock and acquiring the new one, and an updated confirmation email is
  // sent server-side (onAppointmentUpdated) when the time changes.
  const handleReschedule = async () => {
    if (!reschedulingAppt || !selectedSlot || !selectedDate) {
      toast.error('Please select a time slot');
      return;
    }

    // Capacity comes from the schedule that owns the selected slot.
    const ownerSchedule = schedules.find(s => s.userId === selectedSlot.userId);
    const slotCapacity = ownerSchedule?.defaultSlotCapacity ?? 1;

    setSchedulingAppointment(true);
    try {
      await appointmentService.rescheduleAppointment(
        reschedulingAppt.id,
        {
          date: selectedDate,
          startTime: selectedSlot.startTime,
          endTime: selectedSlot.endTime,
          userId: selectedSlot.userId,
          userName: selectedSlot.userName,
          scheduleId: selectedSlot.scheduleId,
          eventType: selectedSlot.eventType || reschedulingAppt.eventType,
        },
        { slotCapacity, changedByName: userProfile?.name || 'User' }
      );

      toast.success('Appointment time updated — an updated confirmation email was sent.');
      closeForm();
      onAppointmentsChange();
    } catch (error) {
      if (error instanceof SlotFullError) {
        toast.error('That slot just filled up — please pick another.');
        await loadSchedulesAndSlots();
      } else {
        console.error('Error rescheduling appointment:', error);
        toast.error('Failed to update appointment time');
      }
    } finally {
      setSchedulingAppointment(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Schedule button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Appointments</h3>
        <button
          onClick={() => {
            setShowScheduleForm(true);
            loadSchedulesAndSlots();
          }}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4" />
          Schedule Follow-up
        </button>
      </div>

      {/* Schedule Form Modal */}
      {showScheduleForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeForm}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {reschedulingAppt ? 'Reschedule Appointment' : 'Schedule Follow-up Appointment'}
              </h3>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {reschedulingAppt ? (
                <>Pick a new time for <strong>{lead.parentName}</strong>. The current slot will be freed and an updated confirmation email sent.</>
              ) : (
                <>Schedule a follow-up appointment for <strong>{lead.parentName}</strong></>
              )}
            </p>

            {availableSlots.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No available time slots</p>
                <p className="text-gray-400 text-sm mt-1">Client users need to set up their availability first</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Date Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Date</label>
                  <div className="flex flex-wrap gap-2">
                    {availableSlots.map(({ date }) => (
                      <button
                        key={date}
                        onClick={() => {
                          setSelectedDate(date);
                          setSelectedSlot(null);
                        }}
                        className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                          selectedDate === date
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                        }`}
                      >
                        {format(new Date(date + 'T12:00:00'), 'EEE, MMM d')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time Slot Selection */}
                {selectedDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Time Slot</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {availableSlots
                        .find(d => d.date === selectedDate)
                        ?.slots.map((slot, idx) => {
                          const slotKey = `${selectedDate}-${slot.startTime}-${slot.endTime}-${slot.userId}-${lead.schoolSegmentId || 'all'}`;
                          const capacity = Math.max(1, slot.capacity ?? 1);
                          const count = slotCounts.get(slotKey) || 0;
                          const isFull = count >= capacity;
                          const showCount = capacity > 1 || count > 0;
                          const isSelected = selectedSlot?.startTime === slot.startTime &&
                            selectedSlot?.endTime === slot.endTime &&
                            selectedSlot?.userId === slot.userId;

                          return (
                            <button
                              key={`${slot.startTime}-${slot.endTime}-${slot.userId}`}
                              onClick={() => !isFull && setSelectedSlot(slot)}
                              disabled={isFull}
                              className={`p-3 text-left rounded-md border transition-colors ${
                                isFull
                                  ? 'bg-red-50 border-red-200 text-red-600 cursor-not-allowed'
                                  : isSelected
                                    ? 'bg-indigo-50 border-indigo-500 ring-2 ring-indigo-500'
                                    : 'bg-white border-gray-300 hover:border-indigo-400'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="font-medium text-gray-900">
                                  {formatTimeRange(slot.startTime, slot.endTime, projectTimezone)}
                                </div>
                                {showCount && (
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                                    isFull
                                      ? 'bg-red-100 text-red-700'
                                      : count > 0
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {count}/{capacity}{isFull ? ' full' : ' booked'}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                with {slot.userName}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {slot.eventType === 'tour' ? 'Tour' : slot.eventType === 'phone' ? 'Phone Call' : 'Meeting'}
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Confirm Button */}
                {selectedSlot && (
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      onClick={closeForm}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={reschedulingAppt ? handleReschedule : handleScheduleAppointment}
                      disabled={schedulingAppointment}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {schedulingAppointment
                        ? (reschedulingAppt ? 'Updating...' : 'Scheduling...')
                        : (reschedulingAppt ? 'Update Time' : 'Schedule Appointment')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Existing Appointments List */}
      {loadingAppointments ? (
        <div className="flex items-center gap-2 text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></div>
          Loading appointments...
        </div>
      ) : appointments.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No appointments scheduled</p>
          <p className="text-gray-400 text-sm mt-1">Click "Schedule Follow-up" to book an appointment</p>
        </div>
      ) : (
        <div className="space-y-3">
          {appointments.map((appt) => {
            const isPast = new Date(appt.date) < new Date();
            const statusColors: Record<string, string> = {
              scheduled: 'bg-blue-100 text-blue-800',
              completed: 'bg-green-100 text-green-800',
              cancelled: 'bg-gray-100 text-gray-800',
              'no-show': 'bg-red-100 text-red-800'
            };

            return (
              <div
                key={appt.id}
                className={`border rounded-lg p-4 ${isPast && appt.status === 'scheduled' ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-gray-200'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${appt.eventType === 'tour' ? 'bg-indigo-100' : 'bg-green-100'}`}>
                      {appt.eventType === 'tour' ? (
                        <MapPin className="w-4 h-4 text-indigo-600" />
                      ) : (
                        <Phone className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {appt.eventType === 'tour' ? 'Tour' : appt.eventType === 'phone' ? 'Phone Call' : 'Meeting'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {format(new Date(appt.date + 'T12:00:00'), 'EEEE, MMMM d, yyyy')}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatTimeRange(appt.startTime, appt.endTime, projectTimezone)}
                      </div>
                      {appt.createdByName && (
                        <div className="text-xs text-gray-400 mt-1">
                          Scheduled by {appt.createdByName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[appt.status] || 'bg-gray-100 text-gray-800'}`}>
                      {appt.status}
                    </span>
                    {appt.status === 'scheduled' && (
                      <button
                        onClick={() => {
                          setReschedulingAppt(appt);
                          setSelectedDate('');
                          setSelectedSlot(null);
                          setShowScheduleForm(true);
                          loadSchedulesAndSlots();
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        <Clock className="w-3.5 h-3.5" />
                        Edit time
                      </button>
                    )}
                  </div>
                </div>
                {isPast && appt.status === 'scheduled' && (
                  <div className="mt-2 text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                    This appointment date has passed - please update the status
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeadScheduleTab;
